import { useMemo } from 'react'

import { useApolloClient } from '@apollo/client'
import { InFilterDefinition } from '@factorialco/f0-react'
import * as Sentry from '@sentry/browser'
import { CurrencyExpenseFilterDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

const FILTER_LABEL = 'reimbursable_currency'

// The priority field is declared non-nullable in the schema but may be null/undefined
// in practice, so the comparator explicitly handles those cases.
export type CurrencyEdge = {
  node: { isoCode: string; name: string; priority: number | null | undefined }
}

export const compareCurrencyEdges = (a: CurrencyEdge, b: CurrencyEdge): number =>
  (a.node.priority ?? Infinity) - (b.node.priority ?? Infinity) ||
  a.node.isoCode.localeCompare(b.node.isoCode)

export const useReimbursableCurrencyFilter = (): InFilterDefinition<string> => {
  const apolloClient = useApolloClient()

  return useMemo(
    () => ({
      label: i18n.t('expenses_v2.forms.fields.reimbursable_currency.label'),
      type: 'in' as const,
      options: {
        cache: true,
        options: async () => {
          try {
            const result = await apolloClient.query({
              query: CurrencyExpenseFilterDocument,
              fetchPolicy: 'cache-first',
            })

            const edges = result.data?.apiCore?.currenciesConnection?.edges ?? []
            return [...edges].sort(compareCurrencyEdges).map((edge) => ({
              label: `${edge.node.isoCode} - ${edge.node.name}`,
              value: edge.node.isoCode,
            }))
          } catch (error) {
            Sentry.captureException(error, {
              tags: { component: 'expenses' },
              extra: { filter: FILTER_LABEL },
            })
            return []
          }
        },
      },
    }),
    [apolloClient]
  )
}
